import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Shield, Lock, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logo from "@/assets/logo-new.png";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast.error("Bitte gib eine gültige E-Mail-Adresse ein");
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert({ email, source: "footer" });

      if (error) {
        if (error.code === "23505") {
          toast.info("Diese E-Mail ist bereits angemeldet");
        } else {
          throw error;
        }
      } else {
        toast.success("Erfolgreich angemeldet! Vielen Dank.");
        setEmail("");
      }
    } catch (error) {
      console.error("Newsletter error:", error);
      toast.error("Ein Fehler ist aufgetreten. Bitte versuche es später.");
    } finally {
      setIsLoading(false);
    }
  };

  return <footer className="relative py-16 bg-gradient-to-b from-secondary/30 to-secondary/60 border-t border-border backdrop-blur-sm overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(34,197,94,0.05),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(59,130,246,0.05),transparent_50%)]" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Love The Nature - Naturkraft Logo" className="w-12 h-12 object-contain" />
              <span className="font-serif font-bold text-lg">Love The Nature</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Ihr umfassender Leitfaden für natürliche Mittel und ganzheitliche Gesundheit. 150+ Naturmittel auf 600+ Seiten.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Shield className="w-4 h-4 text-primary" />
                <span>Sicher</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Lock className="w-4 h-4 text-primary" />
                <span>SSL-Verschlüsselt</span>
              </div>
            </div>
          </div>

          {/* Beliebte Themen */}
          <div className="space-y-4">
            <h3 className="font-bold text-foreground text-sm uppercase tracking-wider">Beliebte Themen</h3>
            <nav className="flex flex-col gap-2.5">
              <Link to="/ratgeber/erkaeltung" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Erkältung natürlich behandeln
              </Link>
              <Link to="/ratgeber/fieber" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Fieber senken
              </Link>
              <Link to="/ratgeber/kopfschmerzen" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Kopfschmerzen lindern
              </Link>
              <Link to="/ratgeber/schlafprobleme" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Schlafprobleme lösen
              </Link>
              <Link to="/ratgeber/verdauung" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Verdauungsprobleme
              </Link>
              <Link to="/ratgeber/stress" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Stress & Entspannung
              </Link>
              <Link to="/ratgeber/immunsystem" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Immunsystem stärken
              </Link>
              <Link to="/ratgeber/gelenkschmerzen" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Gelenkschmerzen
              </Link>
              <Link to="/ratgeber" className="text-sm text-primary hover:underline font-medium">
                → Alle Ratgeber
              </Link>
            </nav>
          </div>

          {/* Rechtliches */}
          <div className="space-y-4">
            <h3 className="font-bold text-foreground text-sm uppercase tracking-wider">Rechtliches</h3>
            <nav className="flex flex-col gap-2.5">
              <Link to="/datenschutz" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Datenschutz
              </Link>
              <Link to="/impressum" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Impressum
              </Link>
              <Link to="/agb" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                AGB
              </Link>
              <Link to="/cookies" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Cookies
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="font-bold text-foreground text-sm uppercase tracking-wider">Kontakt</h3>
            <div className="space-y-2.5">
              <p className="text-sm text-muted-foreground">
                Haben Sie Fragen?
              </p>
              <a href="mailto:osmanbaltaci@hotmail.com" className="text-sm text-primary hover:underline flex items-center gap-2">
                <Mail className="w-4 h-4" />
                osmanbaltaci@hotmail.com
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div className="space-y-4">
            <h3 className="font-bold text-foreground text-sm uppercase tracking-wider">Newsletter</h3>
            <p className="text-sm text-muted-foreground">
              Erhalten Sie exklusive Gesundheitstipps
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
              <Input 
                type="email" 
                placeholder="Ihre E-Mail" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background/50 border-border/50 focus:border-primary" 
                required
              />
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" 
                size="sm"
                disabled={isLoading}
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anmelden"}
              </Button>
            </form>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="py-6 border-t border-border/50">
          <div className="bg-amber-50/50 border border-amber-200/50 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900 leading-relaxed">
                <strong className="font-semibold">Wichtiger Hinweis:</strong> Dieses E-Book dient ausschließlich der Information und ersetzt keine ärztliche Beratung, Diagnose oder Behandlung. Bei gesundheitlichen Beschwerden konsultieren Sie bitte einen Arzt oder Heilpraktiker.
              </div>
            </div>
          </div>
        </div>

        {/* Payment & Trust Badges */}
        <div className="py-8 border-t border-border/50">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              
              
            </div>
            <div className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Osman Baltaci. Alle Rechte vorbehalten.
            </div>
          </div>
        </div>
      </div>
    </footer>;
};
export default Footer;